/**
 * GearListSection — mutations route through event-workspace-api:
 * searchEquipment, putGearItems, patchWorkspace, generateQuoteFromGear,
 * regear.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ApiError } from "@shared/api-client";
import { SectionCard } from "../ui/SectionCard";
import { Banner } from "../ui/Banner";
import { Tooltip } from "../ui/Tooltip";
import { Button } from "../ui/Button";
import { ConfirmModal } from "../ui/ConfirmModal";
import { NumberInput } from "../ui/NumberInput";
import { Select } from "../ui/Select";
import { FolderTab, FolderTabBar } from "../ui/FolderTabs";
import { CalendarClockIcon, PlusIcon, XIcon } from "../ui/icons";
import { useAutosave } from "../../lib/use-autosave";
import { scrollToFirstBlockingNoticeAfterPaint } from "../../lib/scroll-to-blocking-notice";
import {
  useRegisterSavable,
  type SavableSections,
} from "../../lib/use-savable-sections";
import {
  usePublishSectionDirty,
  type DirtySectionsRegistry,
} from "../../lib/use-dirty-sections";
import { cx } from "@shared/cx";
import forms from "../ui/field.module.css";
import { depositFromPercent, formatNZD } from "@shared/nz-money";
import {
  MUTATION_KEYS,
  type MutationRunner,
} from "../../lib/use-event-workspace";
import { validateGearList, validateQuotePackages } from "./validators";
import {
  generateQuoteFromGear,
  putGearItems,
  patchWorkspace,
  regear as regearRequest,
  dismissShortfalls as dismissShortfallsRequest,
  type EquipmentSearchResult,
  type LabourCategoryOption,
  type QuotePackagePayload,
} from "../../lib/event-workspace-api";
import {
  isAvailabilityControlledItem,
  isUnpricedScopeItem,
  isSingleDayItem,
  normalizeBookingItemType,
} from "../../lib/booking-item-types";
import {
  listQuoteTemplates,
  type QuoteTemplate,
} from "../../lib/quote-templates-api";
import {
  FALLBACK_QUOTE_GROUP,
  LABOUR_QUOTE_GROUP,
  MAX_QUOTE_PACKAGES,
  TRANSPORT_QUOTE_GROUP,
  type GearItem,
  type GearItemInput,
  type GearRowClassification,
  type GearShortfall,
  type QuoteDetailsSnapshot,
  type QuotePackage,
} from "../../lib/gear-list-types";
import {
  classifyGearRows,
  computeDateShift,
  equipmentSplitAcrossWarehouses,
  newClientKey,
  normalizeGroupedRows,
  normalizeRowDefaults,
  reseedLogisticsRows,
} from "../../lib/gear-list-rows";
import {
  gearExtraDayRate,
  gearListTotals,
  gearRowDays,
  gearRowWithDays,
  netLineTotal,
} from "../../lib/gear-list-money";
import { multiDayGearRate } from "../../lib/format-utils";
import {
  EquipmentAutocomplete,
  GroupHeadingInput,
  PackageNameInput,
} from "./gear-list-inputs";

// Re-exported so existing importers (event-workspace.tsx, tests) keep their
// import path after the types/helpers moved to ../../lib/gear-list-* (#81).
export {
  FALLBACK_QUOTE_GROUP,
  MAX_QUOTE_PACKAGES,
  type QuotePackage,
  type GearRowClassification,
} from "../../lib/gear-list-types";
import { InlineBanner } from "../ui/InlineBanner";
import {
  ResponsiveTable,
  TableBody,
  TableCell,
  TableFoot,
  TableFullWidthCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../ui/ResponsiveTable";
import cell from "@/components/ui/cell-input.module.css";
import styles from "./GearListSection.module.css";
export {
  classifyGearRows,
  normalizeGroupedRows,
} from "../../lib/gear-list-rows";

/** Extra-day percent label: whole numbers stay whole, else one decimal. */
function formatPercentNumber(n: number): string {
  return n.toLocaleString("en-NZ", { maximumFractionDigits: 1 });
}

interface GearListSectionProps {
  bookingId: string;
  gearItems: GearItemInput[];
  /** The booking's quote packages (#20); empty = classic single list. */
  quotePackages?: QuotePackage[];
  isApproved: boolean;
  state: "active" | "complete" | "future";
  onQuoteGenerated: () => void | Promise<void>;
  onRefresh: () => void | Promise<void>;
  /** Workspace mutation runner — busy state + the shared error sink. */
  mutations: MutationRunner;
  open?: boolean;
  onToggle?: () => void;
  sectionRef?: React.RefObject<HTMLDivElement | null>;
  headerAction?: React.ReactNode;
  initialDepositPercent?: string;
  initialQuoteValidDays?: string;
  initialNotes?: string;
  initialGearDiscountPercent?: string;
  /** Booking-level quote template (#188 follow-on); null inherits the
   * Company default. */
  initialQuoteTemplateId?: string | null;
  eventDayCount?: number;
  eventDate?: string | null;
  eventEndDate?: string | null;
  /**
   * The Company's extra-hire-day percent (`multi_day_extra_day_percent`).
   * Feeds the same multi-day curve the server prices with; omitted falls back
   * to the historic 50%.
   */
  multiDayExtraDayPercent?: number;
  /** Quote-summary generation timestamp — flips the button to "Re-generate". */
  quoteGeneratedAt?: string | null;
  gearShortfalls?: GearShortfall[] | null;
  /**
   * Set when another Booking's approval took gear this one quotes on
   * overlapping dates (#314). Only ever set on a Booking in the `quote`
   * Phase, so the Customer is already holding the affected Quote.
   */
  gearConflictAt?: string | null;
  /**
   * Dirty registry — this section publishes one boolean (unsaved edits, from
   * the autosave fingerprint) so artifact staleness flips the moment an edit
   * exists and holds until the post-save refetch lands. A gear edit is a
   * quote AND job input. See summary-staleness.ts.
   */
  dirtySections?: DirtySectionsRegistry;
  /**
   * Savable registry (#23): job-summary generation flushes all savables
   * first, so pending gear edits land before the summary is built from them.
   */
  savables?: SavableSections;
  // Publishes the live (unsaved) gear rows up so the workspace HiredInBanner
  // can reflect hired-in items the moment they're added, without a save +
  // refetch round-trip.
  onGearItemsChange?: (
    items: {
      item_name: string;
      is_hired_in?: boolean;
      suppliers?: {
        name: string;
        email?: string | null;
        phone?: string | null;
      }[];
    }[],
  ) => void;
  /**
   * The labour vocabulary from the workspace read (quotedLabour.categories,
   * #242 sweep 2 C4) — internal/labour owns the categories and labels; the
   * Labour group's + button seeds the next missing section from this list.
   * Empty (no payload) degrades to seeding blank service rows.
   */
  labourCategories?: LabourCategoryOption[];
  /**
   * booking.items_revision from the workspace read (#242 C2) — the
   * optimistic-concurrency token the gear PUT echoes. Advanced locally from
   * each PUT response and re-synced whenever a fresher read arrives.
   */
  itemsRevision?: number | null;
  /**
   * A PUT came back 409: another writer replaced booking_items since this
   * editor was seeded. Local state is known-stale and unrecoverable, so the
   * workspace refetches and remounts this section to re-seed it.
   */
  onSaveConflict?: () => void | Promise<void>;
  /** Render the conflict notice (workspace-owned so it survives the remount). */
  conflictNotice?: boolean;
  /** Ephemeral dismiss: clears the workspace's conflict-notice state only. */
  onDismissConflictNotice?: () => void;
}

export function GearListSection({
  bookingId,
  gearItems: initialGearItems,
  quotePackages: initialPackages = [],
  isApproved,
  state,
  onQuoteGenerated,
  onRefresh,
  mutations,
  open,
  onToggle,
  sectionRef,
  headerAction,
  initialDepositPercent = "0",
  initialQuoteValidDays = "28",
  initialNotes = "",
  initialGearDiscountPercent = "0",
  initialQuoteTemplateId = null,
  eventDayCount = 1,
  eventDate = null,
  eventEndDate = null,
  multiDayExtraDayPercent,
  quoteGeneratedAt = null,
  gearShortfalls = null,
  gearConflictAt = null,
  dirtySections,
  savables,
  onGearItemsChange,
  labourCategories = [],
  itemsRevision = null,
  onSaveConflict,
  conflictNotice = false,
  onDismissConflictNotice,
}: GearListSectionProps) {
  const [gearItems, setGearItems] = useState<GearItem[]>(() =>
    normalizeGroupedRows(initialGearItems.map(normalizeRowDefaults)),
  );

  // Logistics rows are the server's (#243 sweep 2 D4): the transport compute
  // writes them, the vehicles PUT retires them, and the gear PUT merges this
  // editor's edits into them by id rather than replacing them. So a workspace
  // read carrying a DIFFERENT logistics subset re-seeds that subset here —
  // leaving the Operator's own rows and unsaved edits alone. Gated on the
  // server subset changing, not on local state, so an unrelated refetch never
  // stomps an in-progress transport edit. This is the whole of the deleted
  // refetch-and-remount protocol.
  const serverLogistics = useMemo(
    () =>
      initialGearItems
        .filter((it) => normalizeBookingItemType(it.type) === "logistics")
        .map(normalizeRowDefaults),
    [initialGearItems],
  );
  const serverLogisticsKey = useMemo(
    () =>
      JSON.stringify(
        serverLogistics.map(({ clientKey: _clientKey, ...rest }) => rest),
      ),
    [serverLogistics],
  );
  const seededLogisticsRef = useRef(serverLogisticsKey);
  useEffect(() => {
    if (seededLogisticsRef.current === serverLogisticsKey) return;
    seededLogisticsRef.current = serverLogisticsKey;
    setGearItems((prev) => {
      const next = reseedLogisticsRows(prev, serverLogistics);
      return next === prev ? prev : normalizeGroupedRows(next);
    });
  }, [serverLogisticsKey, serverLogistics]);
  // Quote packages (#20): key = client-minted stable id, name = client-facing
  // label. Empty = the classic single gear list.
  const [packages, setPackages] = useState<QuotePackage[]>(() =>
    initialPackages.map((p) => ({ key: p.key, name: p.name })),
  );
  const [activePackageKey, setActivePackageKey] = useState<string | null>(
    initialPackages.length > 0 ? initialPackages[0].key : null,
  );
  const [packageToDelete, setPackageToDelete] = useState<QuotePackage | null>(
    null,
  );
  // Freshly added (still empty) groups — they only exist in the editor until
  // a row lands in them; group membership itself lives on the rows.
  const [extraGroups, setExtraGroups] = useState<string[]>([]);
  // Loading spans both halves of generation: the save+POST here, then the
  // hook's poll for the new PDF (handleQuoteGenerated).
  const generating =
    mutations.isPending(MUTATION_KEYS.generateQuoteSummary) ||
    mutations.isPending(MUTATION_KEYS.generateQuote);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [depositPercent, setDepositPercent] = useState<number>(() => {
    const n = Number(initialDepositPercent);
    return Number.isFinite(n) ? n : 0;
  });
  const [quoteValidDays, setQuoteValidDays] = useState<number>(() => {
    const n = Number(initialQuoteValidDays);
    return Number.isFinite(n) ? n : 28;
  });
  const [quoteNotes, setQuoteNotes] = useState(initialNotes);
  const [gearDiscountPercent, setGearDiscountPercent] = useState<number>(() => {
    const n = Number(initialGearDiscountPercent);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  });
  // Quote template picker (#188 follow-on). The roster load is best-effort:
  // on failure (or zero templates) the select simply doesn't render and the
  // Booking keeps inheriting. NOT a content-staleness input — template drift
  // has its own snapshot compare (quote_template_stale).
  const [quoteTemplateId, setQuoteTemplateId] = useState<string | null>(
    initialQuoteTemplateId,
  );
  const [quoteTemplates, setQuoteTemplates] = useState<QuoteTemplate[]>([]);
  useEffect(() => {
    const ac = new AbortController();
    listQuoteTemplates(ac.signal)
      .then(setQuoteTemplates)
      .catch(() => {});
    return () => ac.abort();
  }, []);

  // Publish the live gear rows up so the workspace HiredInBanner reflects
  // hired-in items as soon as they're added/removed — before any save. Only
  // the fields the banner reads are forwarded.
  useEffect(() => {
    onGearItemsChange?.(
      gearItems.map((it) => ({
        item_name: it.item_name,
        is_hired_in: it.is_hired_in,
        suppliers: it.suppliers,
      })),
    );
  }, [gearItems, onGearItemsChange]);

  // Optimistic-concurrency echo (#242 C2): seeded from the workspace read,
  // advanced from each PUT response (autosaves are serialised, so the next
  // save always echoes the value the server just returned). A ref, not
  // state: no render reads it.
  const itemsRevisionRef = useRef<number | null>(itemsRevision);
  // A fresher read (post-save refetch, or the refetch after a transport
  // compute) is authoritative: without the pre-D4 remount, the ref is the
  // only thing carrying the echo forward, so it tracks the prop.
  useEffect(() => {
    // Monotonic: the revision only ever counts up, so a read that raced a
    // compute (whose bump reached the ref first via onItemsRevision below)
    // must never drag the echo backwards.
    if (
      itemsRevision != null &&
      (itemsRevisionRef.current == null ||
        itemsRevision > itemsRevisionRef.current)
    )
      itemsRevisionRef.current = itemsRevision;
  }, [itemsRevision]);
  // Latched on the first 409: every later save from this (about-to-be-
  // remounted) instance is a no-op, so the unmount flush can't re-fire the
  // conflict handler and re-remount the freshly seeded editor.
  const saveConflictRef = useRef(false);

  const persistQuoteDetails = useCallback(
    async (snapshot: QuoteDetailsSnapshot) => {
      if (saveConflictRef.current) return;
      const packagePayload: QuotePackagePayload[] = snapshot.packages.map(
        (p, i) => ({ key: p.key, name: p.name, position: i }),
      );
      try {
        const newRevision = await putGearItems(
          bookingId,
          snapshot.gearItems,
          packagePayload,
          itemsRevisionRef.current,
        );
        if (newRevision != null) itemsRevisionRef.current = newRevision;
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          // Another writer (second operator tab, transport compute) replaced
          // booking_items since this editor was seeded. The server refused
          // the overwrite; hand over to the workspace to refetch + remount.
          saveConflictRef.current = true;
          await onSaveConflict?.();
          return;
        }
        throw err;
      }
      await patchWorkspace(bookingId, "quote_settings", {
        notes: snapshot.quoteNotes || null,
        depositPercent: snapshot.depositPercent,
        quoteValidDays: snapshot.quoteValidDays,
        gearDiscountPercent: snapshot.gearDiscountPercent,
        quoteTemplateId: snapshot.quoteTemplateId,
      });
      // Reconcile ws.data with what we just persisted BEFORE the autosave
      // baseline advances: staleness is project(snapshot) vs project(ws.data)
      // OR this section's dirty flag, so the flag must span until this refetch
      // lands or a just-saved edit would briefly read fresh.
      await onRefresh();
    },
    [bookingId, onRefresh, onSaveConflict],
  );

  // The same gate Generate Quote Summary enforces: at least one row (the
  // button is disabled at zero rows), every row named, and every gear row
  // resolved to a real catalogue entry (#364 — no free-typed gear). While it
  // fails, auto-save stays off so half-typed/ad-hoc rows are never persisted.
  const hasInvalidRows = gearItems.some((item) => {
    if (!item.item_name.trim()) return true;
    return isAvailabilityControlledItem(item.type) && !item.equipment_id;
  });

  // Debounced auto-save of quote-detail edits (#320). Edits used to live
  // only in React state until Generate Quote Summary saved them — navigating
  // away first silently dropped everything. A pending change is flushed on
  // unmount; `saveNow` is the button's save-then-generate path.
  const { saveNow: savePendingQuoteDetails, dirty: gearDirty } =
    useAutosave<QuoteDetailsSnapshot>({
      value: {
        gearItems,
        packages,
        depositPercent,
        quoteValidDays,
        quoteNotes,
        gearDiscountPercent,
        quoteTemplateId,
      },
      enabled: gearItems.length > 0 && !hasInvalidRows,
      save: persistQuoteDetails,
    });

  // This section's staleness contribution is one boolean: unsaved edits exist.
  // The autosave fingerprint is the source, so the flag holds through the save
  // round-trip (persistQuoteDetails awaits onRefresh) and the projection
  // compare takes over seamlessly once the refetched read reflects the save.
  usePublishSectionDirty(dirtySections, "gearList", gearDirty);

  // Savable registration (#23): lets TransportSection (and job-summary
  // generation) flush pending quote-detail edits before server-side
  // booking_items writes. Guarded by the same validity gate as autosave so
  // a forced flush never persists half-typed rows.
  useRegisterSavable(savables, "quoteDetails", async () => {
    if (gearItems.length > 0 && !hasInvalidRows) {
      await savePendingQuoteDetails();
    }
  });

  const handleSelectEquipment = (
    index: number,
    equipment: EquipmentSearchResult | null,
    name: string,
  ) => {
    setGearItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        if (equipment) {
          const changedEquipment = equipment.id !== item.equipment_id;
          return {
            ...item,
            item_name: equipment.name,
            equipment_id: equipment.id,
            hire_rate: equipment.hire_rate_per_day,
            manual_override: changedEquipment ? true : item.manual_override,
            // Stamp hired-in metadata so the HiredInBanner reflects this line
            // immediately (no save/refetch needed).
            is_hired_in: equipment.is_hired_in ?? false,
            suppliers: equipment.suppliers ?? [],
          };
        }
        // Free-typed name with no catalog match — can't be hired-in.
        return {
          ...item,
          item_name: name,
          equipment_id: null,
          hire_rate: undefined,
          is_hired_in: false,
          suppliers: [],
        };
      }),
    );
  };

  const handleQuantityChange = (index: number, qty: number) => {
    setGearItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, quantity: Math.max(1, qty) } : item,
      ),
    );
  };

  // Days edits move the row's hire window with them (#227) — the window is
  // what persists and what the quote is priced from; `days` alone has nowhere
  // to be stored and would be discarded on save.
  const handleDaysChange = (index: number, days: number) => {
    setGearItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? gearRowWithDays(item, Math.max(1, days), eventDate)
          : item,
      ),
    );
  };

  const handleRateChange = (index: number, rate: number) => {
    setGearItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, hire_rate: Math.max(0, rate) } : item,
      ),
    );
  };

  const handleNotesChange = (index: number, notes: string) => {
    setGearItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, notes } : item)),
    );
  };

  // ── Quote package handlers (#20) ─────────────────────────────────────────

  const nextPackageName = (existing: QuotePackage[]): string => {
    let n = existing.length + 1;
    let name = `Package ${n}`;
    while (
      existing.some((p) => p.name.trim().toLowerCase() === name.toLowerCase())
    ) {
      name = `Package ${++n}`;
    }
    return name;
  };

  /**
   * Add a package (≤3). On a no-packages booking the first add converts the
   * current list into "Package 1" (all rows adopt its key) and then adds the
   * new package. copyFromKey clones the source package's rows (fresh client
   * keys) into the new one; null starts it empty.
   */
  const handleAddPackage = (copyFromKey: string | null) => {
    // Everything that mints a key or reads current state happens ONCE, here in
    // the event handler — never inside a setState updater. React double-invokes
    // updaters under <StrictMode> in dev; when the updater minted UUIDs and
    // dispatched setGearItems as a side effect, both invocations' setGearItems
    // calls survived with DIFFERENT package keys, so "Duplicate package" cloned
    // the rows twice, half tagged with a key that never reached `packages` (#81).
    if (packages.length >= MAX_QUOTE_PACKAGES) return;

    const firstKey = packages.length === 0 ? newClientKey() : null;
    const base: QuotePackage[] =
      firstKey != null ? [{ key: firstKey, name: "Package 1" }] : packages;
    // When seeding the first package, the copy source is that new Package 1.
    const sourceKey =
      copyFromKey != null && firstKey != null ? firstKey : copyFromKey;
    const next: QuotePackage = {
      key: newClientKey(),
      name: nextPackageName(base),
    };

    setGearItems((rows) => {
      let out =
        firstKey != null
          ? rows.map((r) => ({ ...r, package_key: firstKey }))
          : rows;
      if (sourceKey != null) {
        out = normalizeGroupedRows([
          ...out,
          ...out
            .filter((r) => r.package_key === sourceKey)
            .map((r) => ({
              ...r,
              clientKey: newClientKey(),
              id: undefined,
              package_key: next.key,
            })),
        ]);
      }
      return out;
    });
    setPackages([...base, next]);
    setActivePackageKey(next.key);
  };

  const handleRenamePackage = (key: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setPackages((prev) =>
      prev.map((p) => (p.key === key ? { ...p, name: trimmed } : p)),
    );
  };

  /**
   * Delete a package. A package only means something when the Customer has two
   * to choose between, so a delete that would leave fewer than two collapses
   * the booking back to the classic single list: the survivor's rows keep every
   * field but lose their package_key, and the tab bar disappears. The
   * one-package state is never reachable from here (#309). Above that
   * threshold the deleted package's rows go with it and the rest stay packaged.
   */
  const handleDeletePackage = (key: string) => {
    // Same discipline as handleAddPackage: no setState dispatched from inside a
    // setState updater, so a StrictMode double-invoke cannot double-apply the
    // row rewrite (#81).
    const remaining = packages.filter((p) => p.key !== key);
    if (remaining.length < 2) {
      // Deleting the ONLY package keeps every row (there is no survivor to
      // prefer); deleting one of two keeps the survivor's rows. Either way the
      // rows that stay are un-packaged rather than discarded.
      const survivorKey = remaining.length === 1 ? remaining[0].key : null;
      setGearItems((rows) =>
        normalizeGroupedRows(
          rows
            .filter(
              (r) =>
                survivorKey == null ||
                r.package_key == null ||
                r.package_key === survivorKey,
            )
            .map((r) => ({ ...r, package_key: null })),
        ),
      );
      setPackages([]);
      setActivePackageKey(null);
      return;
    }
    setGearItems((rows) =>
      normalizeGroupedRows(rows.filter((r) => r.package_key !== key)),
    );
    setPackages(remaining);
    setActivePackageKey((active) =>
      active === key ? remaining[0].key : active,
    );
  };

  // Rows visible in the editor: the active package's list (all rows when the
  // booking has no packages). Handlers keep operating on GLOBAL indices.
  const isRowVisible = useCallback(
    (item: GearItem) =>
      activePackageKey == null || item.package_key === activePackageKey,
    [activePackageKey],
  );
  const visibleGearItems = useMemo(
    () => gearItems.filter(isRowVisible),
    [gearItems, isRowVisible],
  );

  // Ordered group headings: first appearance in the (already grouped) visible
  // rows, then any freshly added empty groups. Labour + Transport are
  // auto-offered (their rows are auto-generated and the + seeds the right row
  // type), but like any empty group they can be dismissed with the X; a
  // dismissed group comes back as soon as rows land in it (e.g. a compute).
  const [dismissedGroups, setDismissedGroups] = useState<string[]>([]);
  const groupOrder = useMemo(() => {
    const seen: string[] = [];
    for (const it of visibleGearItems) {
      if (!seen.includes(it.quote_group)) seen.push(it.quote_group);
    }
    for (const g of extraGroups) {
      if (!seen.includes(g)) seen.push(g);
    }
    if (
      !seen.includes(LABOUR_QUOTE_GROUP) &&
      !dismissedGroups.includes(LABOUR_QUOTE_GROUP)
    ) {
      seen.push(LABOUR_QUOTE_GROUP);
    }
    if (
      !seen.includes(TRANSPORT_QUOTE_GROUP) &&
      !dismissedGroups.includes(TRANSPORT_QUOTE_GROUP)
    ) {
      seen.push(TRANSPORT_QUOTE_GROUP);
    }
    return seen;
  }, [visibleGearItems, extraGroups, dismissedGroups]);

  const handleAddRow = (group: string) => {
    setGearItems((prev) => {
      // Labour rows are hours-based services: qty = hours, single-day so the
      // total is hours × rate (Days renders as a dash).
      const isLabour = group === LABOUR_QUOTE_GROUP;
      // Seed the next missing labour section (setup → operation → pack-down →
      // travel) so manual bookings reach the same sections the inquiry
      // compute writes (#181); all present → a blank service row. The
      // vocabulary rides on the workspace read (server-owned, #242 C4).
      const nextSection = isLabour
        ? labourCategories.find(
            (s) =>
              !prev.some(
                (it) =>
                  isRowVisible(it) &&
                  it.quote_group === group &&
                  it.labour_category === s.category,
              ),
          )
        : undefined;
      const row: GearItem = {
        clientKey: newClientKey(),
        item_name: nextSection?.label ?? "",
        labour_category: nextSection?.category ?? null,
        quantity: 1,
        days: isLabour ? 1 : eventDayCount,
        type: isLabour ? "service" : undefined,
        notes: "",
        quote_group: group,
        discount: 0,
        // Inherit the group's summarise state so the flag stays uniform.
        quote_group_summarised: prev.some(
          (it) => it.quote_group === group && it.quote_group_summarised,
        ),
        package_key: activePackageKey,
        sort_order: prev.length,
      };
      // Insert after the group's last row (within the active package) so the
      // new line lands in its group.
      let insertAt = prev.length;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].quote_group === group && isRowVisible(prev[i])) {
          insertAt = i + 1;
          break;
        }
      }
      const next = [...prev.slice(0, insertAt), row, ...prev.slice(insertAt)];
      return normalizeGroupedRows(next);
    });
  };

  const handleAddGroup = () => {
    let name = "New group";
    let n = 2;
    while (groupOrder.includes(name)) name = `New group ${n++}`;
    setExtraGroups((prev) => [...prev, name]);
  };

  const handleRemoveEmptyGroup = (group: string) => {
    setExtraGroups((prev) => prev.filter((g) => g !== group));
    // Also covers the auto-offered Labour/Transport groups, which aren't in
    // extraGroups — without this they'd be re-injected on the next render.
    setDismissedGroups((prev) =>
      prev.includes(group) ? prev : [...prev, group],
    );
  };

  const handleRenameGroup = (from: string, to: string) => {
    const name = to.trim();
    if (!name || name === from) return;
    setGearItems((prev) =>
      normalizeGroupedRows(
        prev.map((it) =>
          it.quote_group === from && isRowVisible(it)
            ? { ...it, quote_group: name }
            : it,
        ),
      ),
    );
    setExtraGroups((prev) => {
      const renamed = prev.map((g) => (g === from ? name : g));
      return renamed.filter((g, i) => renamed.indexOf(g) === i);
    });
  };

  const handleMoveRowToGroup = (index: number, group: string) => {
    setGearItems((prev) => {
      // A moved row adopts the destination group's summarise state.
      const destSummarised = prev.some(
        (it, i) =>
          i !== index &&
          it.quote_group === group &&
          isRowVisible(it) &&
          it.quote_group_summarised,
      );
      return normalizeGroupedRows(
        prev.map((it, i) =>
          i === index
            ? {
                ...it,
                quote_group: group,
                quote_group_summarised: destSummarised,
              }
            : it,
        ),
      );
    });
  };

  // Per-section summarise toggle: sets the flag uniformly across the group's
  // rows so generation snapshots a consistent collapsed/itemized face.
  const handleToggleGroupSummarised = (group: string, next: boolean) => {
    setGearItems((prev) =>
      prev.map((it) =>
        it.quote_group === group && isRowVisible(it)
          ? { ...it, quote_group_summarised: next }
          : it,
      ),
    );
  };

  const handleDeleteRow = (index: number) => {
    setGearItems((prev) =>
      normalizeGroupedRows(prev.filter((_, i) => i !== index)),
    );
  };

  const [regearing, setRegearing] = useState(false);
  const [regearError, setRegearError] = useState<string | null>(null);
  const handleRegear = async () => {
    setRegearing(true);
    setRegearError(null);
    try {
      // Flush any pending quote-detail edits before the re-pick: the autosave
      // debounce could otherwise fire mid/after regear and its full-replace
      // PUT would resurrect the pre-regear rows. saveNow cancels the pending
      // timer and the await sequences the PUT strictly before the regear.
      if (gearItems.length > 0 && !hasInvalidRows) {
        await savePendingQuoteDetails();
      }
      await regearRequest(bookingId);
      // Regear is synchronous on the server (DB writes are committed when
      // the API returns), so a plain refetch is correct — matches the
      // convention used by every other mutation in use-event-workspace.ts.
      // onQuoteGenerated polls for a NEW quote PDF that regear doesn't
      // produce, leaving the UI in a 90s loading state with stale gear.
      await onRefresh();
    } catch (e) {
      setRegearError(e instanceof Error ? e.message : "Re-pick failed");
    } finally {
      setRegearing(false);
    }
  };

  const [dismissingShortfalls, setDismissingShortfalls] = useState(false);
  const handleDismissShortfalls = async () => {
    setDismissingShortfalls(true);
    try {
      await dismissShortfallsRequest(bookingId);
      await onRefresh();
    } catch {
      // Soft-fail — operator can retry. No banner spawn for a dismiss button.
    } finally {
      setDismissingShortfalls(false);
    }
  };

  const dateShift = computeDateShift(gearItems, eventDate, eventEndDate);

  const shortfallList = gearShortfalls ?? [];
  const splitEquipmentIds = useMemo(
    () => equipmentSplitAcrossWarehouses(gearItems),
    [gearItems],
  );
  const rowClassifications = classifyGearRows(gearItems);
  const activePackage =
    packages.find((p) => p.key === activePackageKey) ?? null;

  // Stable per-row identity used both as the React key and the validator map
  // key, so each row's item-name error lines up with the row it belongs to.
  const rowKey = (item: GearItem, index: number) =>
    item.clientKey || `idx-${index}`;

  // Reveal-then-live: recomputed every render off current rows, so each row's
  // message clears the moment its name is filled. `showValidation` only toggles
  // visibility — keystrokes never blank the map.
  const itemNameErrors = validateGearList(
    gearItems.map((item, index) => ({
      key: rowKey(item, index),
      item_name: item.item_name,
      type: item.type,
      equipment_id: item.equipment_id,
    })),
  );

  // Package layout errors (#20) — same rules generation enforces with a 400.
  // Recomputed per render; `showValidation` gates visibility.
  const packageErrors = validateQuotePackages(
    packages,
    gearItems.map((it) => ({ package_key: it.package_key })),
  );

  const handleGenerateQuoteClick = () => {
    if (isApproved) {
      setShowRegenConfirm(true);
    } else {
      void doGenerateQuote();
    }
  };

  const doGenerateQuote = async () => {
    if (hasInvalidRows || packageErrors.length > 0) {
      setShowValidation(true);
      setShowRegenConfirm(false);
      // A blocking row can sit in a Package the Operator is not looking at,
      // where revealing its notice shows them nothing at all — switch to it
      // before scrolling, or the page moves to the wrong list.
      const offending = gearItems.find(
        (item) =>
          !item.item_name.trim() ||
          (isAvailabilityControlledItem(item.type) && !item.equipment_id),
      );
      if (offending && offending.package_key !== activePackageKey) {
        setActivePackageKey(offending.package_key ?? null);
      }
      scrollToFirstBlockingNoticeAfterPaint();
      return;
    }
    setShowRegenConfirm(false);
    // Save-first, then generate — unchanged behaviour. saveNow cancels any
    // pending debounce so the same snapshot isn't persisted twice. No refetch
    // here: onQuoteGenerated polls for the new PDF, which IS the refetch.
    const ok = await mutations.run(
      MUTATION_KEYS.generateQuoteSummary,
      "Generate quote summary",
      async () => {
        await savePendingQuoteDetails();
        await generateQuoteFromGear(bookingId);
      },
      { refetch: false },
    );
    if (!ok) return;
    await onQuoteGenerated();
  };

  // Per-line and total money math live in ../../lib/gear-list-money (#81).
  const extraDayRate = gearExtraDayRate(multiDayExtraDayPercent);
  // Derived from the pricing function rather than the raw percent so the
  // tooltip cannot disagree with the math: multiDayGearRate clamps an
  // out-of-range rate back to the 50% default, and day two's share IS that
  // rate.
  const extraDayPercentLabel = `${formatPercentNumber(
    (multiDayGearRate(2, extraDayRate) - 1) * 100,
  )}%`;
  // Totals are scoped to the visible list — the active package's complete
  // line set on a packaged booking (#20), matching that package's quote PDF.
  const { totalCost, gearDiscountAmount } = gearListTotals(
    visibleGearItems,
    gearDiscountPercent,
    extraDayRate,
  );

  const summary =
    packages.length > 0
      ? `${packages.length} package${packages.length !== 1 ? "s" : ""} · ${gearItems.length} item${gearItems.length !== 1 ? "s" : ""}`
      : `${gearItems.length} item${gearItems.length !== 1 ? "s" : ""}${totalCost > 0 ? ` · ${formatNZD(totalCost)}` : ""}`;

  // The column labels below sm, the mobile card row, and the corner delete
  // are ResponsiveTable's — this section used to carry its own copy of all
  // three. INVARIANT: no overflow-* on any ancestor of EquipmentAutocomplete
  // (its dropdown is position:absolute top-full) or the dropdown re-clips,
  // which is why the table stays on the cards pattern and never x-auto.

  return (
    <SectionCard
      title="Quote Details"
      collapsible
      open={open}
      onToggle={onToggle}
      sectionRef={sectionRef}
      state={state}
      summary={summary}
      headerAction={headerAction}
    >
      {/* Ephemeral dismiss: the X clears the workspace's conflict-notice
          state only (#242 C2). The condition itself is already resolved —
          the 409'd save was refused and this editor re-seeded from the
          server — so the banner is a report, not a live warning. */}
      {conflictNotice && (
        <Banner
          variant="warning"
          className={styles.banner}
          title="Gear list reloaded"
          onDismiss={onDismissConflictNotice}
          dismissLabel="Dismiss conflict notice"
        >
          Someone else updated this gear list while you were editing, so your
          unsaved changes were not applied. The list below is the latest version
          — re-apply any edits you still need.
        </Banner>
      )}

      {/* Acknowledged dismiss: the X permanently clears bookings.gear_shortfalls
          via POST /shortfalls/dismiss — it does not come back on reload. */}
      {shortfallList.length > 0 && (
        <Banner
          variant="warning"
          className={styles.banner}
          title="Gear shortfall after pick"
          onDismiss={handleDismissShortfalls}
          dismissing={dismissingShortfalls}
          dismissLabel="Dismiss shortfall notice"
        >
          <ul className={styles.bannerList}>
            {shortfallList.map((s) => {
              // The server resolves the Role's name from the Booking's
              // Profile (#21) — admin carries no role catalogue of its own.
              const label = s.role_label ?? s.role;
              return (
                <li key={s.role}>
                  {label}: {s.qty_short}× short, chain exhausted on the selected
                  dates.
                </li>
              );
            })}
          </ul>
          <p className={styles.bannerNote}>
            Quote is still sendable. Resolve via sub-hire, schedule shift, or a
            scope conversation.
          </p>
        </Banner>
      )}

      {/* Resolved by action: no X — the re-pick clears gear_conflict_at
          server-side (#314). Only a Booking in the quote Phase can carry this
          stamp, so the Customer is already holding the affected Quote. */}
      {gearConflictAt && (
        <Banner
          variant="warning"
          className={styles.banner}
          title="Gear taken by an approved booking"
        >
          <p className={styles.bannerLead}>
            Another Booking was approved for gear this quote holds on these
            dates. This quote has already been sent to the Customer.
          </p>
          {regearError && (
            <p className={styles.bannerError}>{regearError}</p>
          )}
          <Button
            size="sm"
            variant="secondary"
            loading={regearing}
            onClick={handleRegear}
            className={styles.bannerAction}
          >
            Re-pick available gear
          </Button>
        </Banner>
      )}

      {/* Resolved by action: no X — re-picking gear clears the condition. */}
      {dateShift && (
        <Banner
          variant="info"
          className={styles.banner}
          title="Booking dates changed"
          icon={<CalendarClockIcon className={styles.bannerIcon} />}
        >
          <p className={styles.bannerLead}>
            Gear is still reserved for {dateShift.rowStart}
            {dateShift.rowEnd !== dateShift.rowStart
              ? ` to ${dateShift.rowEnd}`
              : ""}
            . Event is now {dateShift.bookingStart}
            {dateShift.bookingEnd !== dateShift.bookingStart
              ? ` to ${dateShift.bookingEnd}`
              : ""}
            .
          </p>
          {regearError && (
            <p className={styles.bannerError}>{regearError}</p>
          )}
          <Button
            size="sm"
            variant="secondary"
            loading={regearing}
            onClick={handleRegear}
            className={styles.bannerAction}
          >
            Re-pick gear for new dates
          </Button>
          <p className={styles.bannerNote}>
            Manual-override rows are preserved; auto-picked rows are replaced.
          </p>
        </Banner>
      )}

      {packages.length > 0 && (
        <FolderTabBar ariaLabel="Quote packages" wrap className={styles.tabs}>
          {packages.map((pkg) => {
            const selected = activePackageKey === pkg.key;
            // The selected tab hosts interactive children (rename input +
            // delete button), so it renders as a non-button FolderTab.
            return selected ? (
              <FolderTab key={pkg.key} selected surface="card">
                <PackageNameInput
                  value={pkg.name}
                  onRename={(next) => handleRenamePackage(pkg.key, next)}
                />
                <button
                  type="button"
                  // The sole package loses nothing on delete — its rows come
                  // back as the plain list — so there is nothing to confirm.
                  onClick={() =>
                    packages.length <= 1
                      ? handleDeletePackage(pkg.key)
                      : setPackageToDelete(pkg)
                  }
                  aria-label={`Delete ${pkg.name}`}
                  className={styles.tabDelete}
                >
                  <XIcon className={styles.tabDeleteIcon} />
                </button>
              </FolderTab>
            ) : (
              <FolderTab
                key={pkg.key}
                selected={false}
                surface="card"
                onSelect={() => setActivePackageKey(pkg.key)}
              >
                {pkg.name}
              </FolderTab>
            );
          })}
        </FolderTabBar>
      )}

      <ResponsiveTable>
        <TableHead>
          <TableHeaderCell>Item</TableHeaderCell>
          <TableHeaderCell>Notes</TableHeaderCell>
          <TableHeaderCell width="28">
            <Tooltip content="The quote heading this line renders under.">
              Group
            </Tooltip>
          </TableHeaderCell>
          <TableHeaderCell width="14">Qty</TableHeaderCell>
          <TableHeaderCell width="14">
            <Tooltip
              content={`Calendar days on hire. Day one charges full, each extra day ${extraDayPercentLabel}.`}
            >
              Days
            </Tooltip>
          </TableHeaderCell>
          <TableHeaderCell width="20">
            <Tooltip content="Hire rate for one unit for one day — transport instead is one round trip, the zone rate for the km from your warehouse, doubled.">
              Rate
            </Tooltip>
          </TableHeaderCell>
          <TableHeaderCell align="right" width="20">
            <Tooltip content="Qty × rate × chargeable days.">Total</Tooltip>
          </TableHeaderCell>
          <TableHeaderCell width="10" padding="none" />
        </TableHead>
        <TableBody>
          {groupOrder.map((group) => {
            const groupRows = gearItems
              .map((item, index) => ({ item, index }))
              .filter(
                ({ item }) => item.quote_group === group && isRowVisible(item),
              );
            const groupTotal = groupRows.reduce(
              (sum, { item }) => sum + netLineTotal(item, extraDayRate),
              0,
            );
            const groupSummarised =
              groupRows.length > 0 &&
              groupRows.every(({ item }) => item.quote_group_summarised);
            return (
              <Fragment key={group}>
                <TableRow variant="plain" className={styles.groupRow}>
                  <TableFullWidthCell
                    colSpan={8}
                    className={styles.groupHeadingCell}
                  >
                    <div className={styles.groupHead}>
                      <GroupHeadingInput
                        value={group}
                        onRename={(next) => handleRenameGroup(group, next)}
                      />
                      <div className={styles.groupActions}>
                        <button
                          type="button"
                          onClick={() => handleAddRow(group)}
                          aria-label={`Add item to ${group}`}
                          className={styles.iconButton}
                        >
                          <PlusIcon className={styles.icon} />
                        </button>
                        {groupRows.length > 0 &&
                          (groupRows.every(
                            ({ item }) =>
                              normalizeBookingItemType(item.type) === "service",
                          ) ? (
                            // All-labour groups always collapse on the client
                            // quote (#181) — generation forces the flag, so
                            // the choice is removed here.
                            <span className={styles.summarisedNote}>
                              <Tooltip content="Labour always shows on the quote as one total with combined hours.">
                                Summarised on quote
                              </Tooltip>
                            </span>
                          ) : (
                            <label className={styles.summariseToggle}>
                              <input
                                type="checkbox"
                                aria-label={`Summarise ${group} on quote`}
                                checked={groupSummarised}
                                onChange={(e) =>
                                  handleToggleGroupSummarised(
                                    group,
                                    e.target.checked,
                                  )
                                }
                                className={styles.checkbox}
                              />
                              <Tooltip content="The quote shows this section as a heading and price, with no per-item pricing.">
                                Summarise
                              </Tooltip>
                            </label>
                          ))}
                        {groupRows.length === 0 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveEmptyGroup(group)}
                            aria-label={`Remove empty group ${group}`}
                            className={styles.iconButton}
                          >
                            <XIcon className={styles.icon} />
                          </button>
                        )}
                      </div>
                    </div>
                  </TableFullWidthCell>
                </TableRow>
                {groupRows.map(({ item, index }) => {
                  const lineTotal = netLineTotal(item, extraDayRate);
                  const isGearType = isAvailabilityControlledItem(item.type);
                  // Service and scope rows: qty already spans the job, so they
                  // price single-day and the Days cell is an un-editable dash.
                  const isSingleDayRow = isSingleDayItem(item.type);
                  // Transport rows (`logistics`) price single-day like
                  // services; otherwise they are ordinary editable rows.
                  const isTransportRow =
                    normalizeBookingItemType(item.type) === "logistics";
                  const dash = <span className={styles.dash}>—</span>;
                  const itemNameError = itemNameErrors[rowKey(item, index)];
                  const showItemNameError =
                    showValidation && Boolean(itemNameError);
                  return (
                    // Mobile: a self-contained card — item name + notes
                    // full-width, the numeric fields in a compact 2-per-row
                    // grid, delete in the top-right corner. sm+ reverts to the
                    // plain table row. (No overflow-* —
                    // EquipmentAutocomplete's dropdown must escape.)
                    <TableRow key={rowKey(item, index)}>
                      <TableCell
                        label="Item"
                        span={4}
                        padding="tight"
                        cornerGap="sm"
                      >
                        {isGearType ? (
                          <EquipmentAutocomplete
                            value={item.item_name}
                            onSelect={(eq, name) => {
                              handleSelectEquipment(index, eq, name);
                            }}
                            placeholder="Search gear..."
                            invalid={showItemNameError}
                            ariaLabel="Item"
                          />
                        ) : (
                          <input
                            type="text"
                            aria-label="Item"
                            value={item.item_name}
                            aria-invalid={showItemNameError || undefined}
                            onChange={(e) => {
                              setGearItems((prev) =>
                                prev.map((it, i) =>
                                  i === index
                                    ? { ...it, item_name: e.target.value }
                                    : it,
                                ),
                              );
                            }}
                            className={cx(cell.cell, showItemNameError && cell.invalid)}
                          />
                        )}
                        {showItemNameError && (
                          <InlineBanner variant="error" placement="cell">
                            {itemNameError}
                          </InlineBanner>
                        )}
                        {rowClassifications[index] === "split" && (
                          <InlineBanner variant="info" placement="cell">
                            Split across models to meet quantity.
                          </InlineBanner>
                        )}
                        {/* Why this item occupies two rows (#304). Only on the
                            rows actually split, and only naming the site —
                            every row carries a Warehouse, so annotating them
                            all would say nothing. */}
                        {item.equipment_id &&
                          item.warehouse_name &&
                          splitEquipmentIds.has(item.equipment_id) && (
                            <InlineBanner variant="info" placement="cell">
                              From {item.warehouse_name}.
                            </InlineBanner>
                          )}
                        {rowClassifications[index] === "substituted" &&
                          item.substituted_from_name && (
                            <InlineBanner variant="warning" placement="cell">
                              Used {item.item_name}, preferred{" "}
                              {item.substituted_from_name} unavailable on these
                              dates.
                            </InlineBanner>
                          )}
                        {/* Row-level annotation, like the split/substituted
                            notes above — the Rate column is too narrow to hold
                            a sentence without blowing the grid out. The Rate
                            input keeps the ring as its field indicator (#100). */}
                        {isUnpricedScopeItem(item) && (
                          <InlineBanner variant="error" placement="cell">
                            Needs a price before the quote summary can be
                            generated
                          </InlineBanner>
                        )}
                      </TableCell>
                      <TableCell label="Notes" padding="tight">
                        <input
                          type="text"
                          aria-label="Notes"
                          value={item.notes || ""}
                          onChange={(e) =>
                            handleNotesChange(index, e.target.value)
                          }
                          className={cell.cell}
                        />
                      </TableCell>
                      {/* Capped and truncating: a long group heading otherwise
                          sizes the column to its widest option and pushes its
                          own chevron over the Qty cell. */}
                      <TableCell
                        label="Group"
                        padding="tight"
                        className={styles.groupCell}
                      >
                        <select
                          aria-label="Group"
                          value={item.quote_group}
                          onChange={(e) =>
                            handleMoveRowToGroup(index, e.target.value)
                          }
                          className={cell.cellSelect}
                        >
                          {groupOrder.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell label="Qty" padding="tight">
                        <NumberInput
                          value={item.quantity}
                          onChange={(n) => handleQuantityChange(index, n)}
                          min={1}
                          aria-label="Quantity"
                          className={cell.cell}
                        />
                      </TableCell>
                      <TableCell label="Days" padding="tight">
                        {isSingleDayRow || isTransportRow ? (
                          dash
                        ) : (
                          <NumberInput
                            value={gearRowDays(item)}
                            onChange={(n) => handleDaysChange(index, n)}
                            min={1}
                            aria-label="Days"
                            className={cell.cell}
                          />
                        )}
                      </TableCell>
                      <TableCell label="Rate" padding="tight">
                        {/* null (never 0) for an unset rate: an unpriced scope
                            row (#100) must read as empty, not as $0, and the
                            server blocks send-to-client until it gets a rate. */}
                        <NumberInput
                          value={item.hire_rate ?? null}
                          onChange={(n) => handleRateChange(index, n)}
                          min={0}
                          step="0.01"
                          aria-label="Rate"
                          className={cx(
                            cell.cell,
                            isUnpricedScopeItem(item) && styles.unpriced,
                          )}
                        />
                      </TableCell>
                      <TableCell
                        label="Total"
                        align="right"
                        padding="tight"
                        className={styles.lineTotal}
                      >
                        {lineTotal !== 0 ? formatNZD(lineTotal) : "-"}
                      </TableCell>
                      <TableCell corner padding="tight">
                        <button
                          onClick={() => handleDeleteRow(index)}
                          aria-label="Remove gear row"
                          className={styles.iconButton}
                        >
                          <XIcon className={styles.icon} />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {groupRows.length > 0 && (
                  <TableRow variant="plain">
                    <TableFullWidthCell
                      colSpan={8}
                      className={styles.groupTotalCell}
                    >
                      <Tooltip content="The sum of this group's line totals.">
                        Total for {group}: {formatNZD(groupTotal)}
                      </Tooltip>
                    </TableFullWidthCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
        {totalCost > 0 && (
          <TableFoot>
            <TableRow variant="plain" className={styles.totalRow}>
              <TableCell colSpan={4} className={styles.totalLabel}>
                <Tooltip
                  content={
                    activePackage
                      ? "All lines in this package, net of discounts, before GST."
                      : "All groups combined, net of discounts, before GST."
                  }
                >
                  {activePackage ? `Total — ${activePackage.name}` : "Total"}
                </Tooltip>
              </TableCell>
              <TableCell
                colSpan={4}
                align="right"
                className={styles.totalAmount}
              >
                {gearDiscountAmount > 0 ? (
                  <span className={styles.totalBreakdown}>
                    <span>{formatNZD(totalCost - gearDiscountAmount)}</span>
                    <span className={styles.totalWorking}>
                      ({formatNZD(totalCost)} − {formatNZD(gearDiscountAmount)})
                    </span>
                  </span>
                ) : (
                  formatNZD(totalCost)
                )}
              </TableCell>
            </TableRow>
          </TableFoot>
        )}
      </ResponsiveTable>

      <div className={styles.addRow}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            handleAddRow(
              groupOrder[groupOrder.length - 1] ?? FALLBACK_QUOTE_GROUP,
            )
          }
        >
          <span className={styles.addLabel}>
            <PlusIcon className={styles.icon} />
            Add gear
          </span>
        </Button>
        <Button variant="ghost" size="sm" onClick={handleAddGroup}>
          <span className={styles.addLabel}>
            <PlusIcon className={styles.icon} />
            Add group
          </span>
        </Button>
        {/* A button's label is its own affordance: what these do is named on
            them, so none carries a tooltip (DESIGN-SYSTEM.md, Tooltips). */}
        {packages.length === 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleAddPackage("seed-from-current")}
            disabled={gearItems.length === 0}
          >
            <span className={styles.addLabel}>
              <PlusIcon className={styles.icon} />
              Split into packages
            </span>
          </Button>
        ) : (
          packages.length < MAX_QUOTE_PACKAGES && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleAddPackage(null)}
              >
                <span className={styles.addLabel}>
                  <PlusIcon className={styles.icon} />
                  Add package
                </span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleAddPackage(activePackageKey)}
              >
                <span className={styles.addLabel}>
                  <PlusIcon className={styles.icon} />
                  Duplicate package
                </span>
              </Button>
            </>
          )
        )}
      </div>

      <div className={styles.terms}>
        <div className={styles.termsGrid}>
          <div>
            <label className={forms.label}>
              <Tooltip content="Applies to gear lines only — labour and transport are not discounted.">
                Equipment discount %
              </Tooltip>
            </label>
            <NumberInput
              value={gearDiscountPercent}
              onChange={(n) =>
                setGearDiscountPercent(Math.max(0, Math.min(100, n)))
              }
              min={0}
              max={100}
              fallback={0}
              className={forms.input}
            />
          </div>
          <div>
            <label className={forms.label}>
              <Tooltip content="The share of the total invoiced as the deposit when the Customer approves.">
                Deposit %
              </Tooltip>
            </label>
            <NumberInput
              value={depositPercent}
              onChange={(n) => setDepositPercent(n)}
              min={0}
              max={100}
              fallback={50}
              className={forms.input}
            />
            {gearItems.length > 0 && (
              <p className={styles.hint}>
                Deposit:{" "}
                {formatNZD(
                  depositFromPercent(
                    totalCost - gearDiscountAmount,
                    depositPercent,
                  ),
                )}
              </p>
            )}
          </div>
          <div>
            <label className={forms.label}>
              Valid for (days)
            </label>
            <NumberInput
              value={quoteValidDays}
              onChange={(n) => setQuoteValidDays(n)}
              min={1}
              max={90}
              fallback={28}
              className={forms.input}
            />
          </div>
        </div>
        {quoteTemplates.length > 0 && (
          <div>
            <label className={forms.label} htmlFor="quote-template-select">
              <Tooltip content="Which sections this Booking's quote and invoices include.">
                Quote template
              </Tooltip>
            </label>
            <Select
              id="quote-template-select"
              ariaLabel="Quote template"
              value={quoteTemplateId ?? ""}
              onChange={(next) => setQuoteTemplateId(next || null)}
              options={[
                {
                  value: "",
                  label: `Default (${
                    quoteTemplates.find((t) => t.is_default)?.name ??
                    "Full quote"
                  })`,
                },
                ...quoteTemplates.map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
          </div>
        )}
        <div>
          <label className={forms.label}>
            Notes for client{" "}
            <span className={forms.optional}>(optional)</span>
          </label>
          <textarea
            value={quoteNotes}
            onChange={(e) => setQuoteNotes(e.target.value)}
            rows={2}
            className={cx(forms.input, styles.resizable)}
          />
        </div>
        {showValidation && packageErrors.length > 0 && (
          <Banner variant="error" title="Fix the packages before generating:">
            <ul className={styles.bannerList}>
              {packageErrors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </Banner>
        )}
        <Button
          onClick={handleGenerateQuoteClick}
          loading={generating}
          disabled={gearItems.length === 0}
          className={styles.fullWidth}
        >
          {quoteGeneratedAt
            ? "Re-generate Quote Summary"
            : "Generate Quote Summary"}
        </Button>
      </div>

      <ConfirmModal
        open={packageToDelete != null}
        title={`Delete ${packageToDelete?.name ?? "package"}`}
        message="Its gear lines are deleted. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={() => {
          if (packageToDelete) handleDeletePackage(packageToDelete.key);
          setPackageToDelete(null);
        }}
        onCancel={() => setPackageToDelete(null)}
      />

      <ConfirmModal
        open={showRegenConfirm}
        title="Regenerate Quote Summary"
        message="This quote has already been approved by the client. Regenerating updates the admin preview only. The client's existing approval and PDF stay live. Re-send the quote from Quote Summary to push the new version to the client."
        confirmLabel="Regenerate"
        confirmVariant="primary"
        loading={generating}
        onConfirm={doGenerateQuote}
        onCancel={() => setShowRegenConfirm(false)}
      />
    </SectionCard>
  );
}
